/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */
// Update mobile price when variant changes
document.addEventListener('on:variant:change', function(event) {
  const variant = event.detail.variant;
  const mobilePrice = document.querySelector('[data-mobile-price]');

  if (mobilePrice && variant) {
    const priceContainer = mobilePrice.querySelector('.price');
    if (priceContainer) {
      const currentPrice = priceContainer.querySelector('.price__current');
      const wasPrice = priceContainer.querySelector('.price__was');
      const discountBadge = priceContainer.querySelector('.price__discount');

      // Format price using Shopify's money format
      const formatMoney = (cents) => {
        if (window.theme && window.theme.moneyFormat) {
          return window.theme.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (cents / 100).toFixed(2));
        }
        return '$' + (cents / 100).toFixed(2);
      };

      // Update current price
      if (currentPrice) {
        currentPrice.textContent = formatMoney(variant.price);
      }

      // Handle sale price
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        priceContainer.classList.add('price--on-sale');
        if (wasPrice) {
          wasPrice.textContent = formatMoney(variant.compare_at_price);
          wasPrice.style.display = '';
        }
        // Update discount percentage
        if (discountBadge) {
          const discount = Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price);
          discountBadge.textContent = '(' + discount + '% OFF)';
          discountBadge.style.display = '';
        }
      } else {
        priceContainer.classList.remove('price--on-sale');
        if (wasPrice) wasPrice.style.display = 'none';
        if (discountBadge) discountBadge.style.display = 'none';
      }
    }
  }
});

// Preserve selected size when switching product colors
document.addEventListener('DOMContentLoaded', function() {
  // Save selected size when clicking product color
  const productColorLinks = document.querySelectorAll('.variant_picker-product-color .product-color a');
  productColorLinks.forEach(function(link) {
    link.addEventListener('click', function(e) {
      const selectedSizeInput = document.querySelector('.option-selector input[type="radio"]:checked');
      if (selectedSizeInput) {
        localStorage.setItem('eeveve_selected_size', selectedSizeInput.value);
      }
    });
  });

  // Apply saved size on page load after variant-picker is ready
  function applySavedSize() {
    const savedSize = localStorage.getItem('eeveve_selected_size');
    if (savedSize) {
      const sizeInput = document.querySelector('.option-selector input[type="radio"][value="' + savedSize + '"]');
      if (sizeInput) {
        sizeInput.checked = true;
        sizeInput.dispatchEvent(new Event('change', { bubbles: true }));
        localStorage.removeItem('eeveve_selected_size');
      }
    }
  }

  // Wait for variant-picker to be loaded
  const variantPicker = document.querySelector('variant-picker');
  if (variantPicker) {
    if (variantPicker.hasAttribute('loaded')) {
      applySavedSize();
    } else {
      const observer = new MutationObserver(function(mutations) {
        if (variantPicker.hasAttribute('loaded')) {
          observer.disconnect();
          applySavedSize();
        }
      });
      observer.observe(variantPicker, { attributes: true });
    }
  } else {
    applySavedSize();
  }
});

/* ============================================
   Color Swatches Popup Functionality
   ============================================ */
(function() {
  'use strict';

  function initColorPopup() {
    // Get all popup triggers (variant-based)
    const popupTriggers = document.querySelectorAll('[data-color-popup-trigger]');

    popupTriggers.forEach(function(trigger) {
      trigger.addEventListener('click', function(e) {
        e.preventDefault();
        const popupId = this.getAttribute('data-color-popup-trigger');
        const popup = document.querySelector('[data-color-popup="' + popupId + '"]');

        if (popup) {
          openPopup(popup);
        }
      });
    });

    // Get all linked color popup triggers (metafield-based)
    const linkedPopupTriggers = document.querySelectorAll('[data-linked-color-popup-trigger]');

    linkedPopupTriggers.forEach(function(trigger) {
      trigger.addEventListener('click', function(e) {
        e.preventDefault();
        const popup = document.querySelector('[data-linked-color-popup]');

        if (popup) {
          openPopup(popup);
        }
      });
    });

    // Close popup on overlay click
    const popupOverlays = document.querySelectorAll('.color-popup-overlay');
    popupOverlays.forEach(function(overlay) {
      overlay.addEventListener('click', function(e) {
        if (e.target === this) {
          closePopup(this);
        }
      });
    });

    // Close popup on close button click
    const closeButtons = document.querySelectorAll('.color-popup-close');
    closeButtons.forEach(function(btn) {
      btn.addEventListener('click', function() {
        const popup = this.closest('.color-popup-overlay');
        if (popup) {
          closePopup(popup);
        }
      });
    });

    // Close popup when selecting a color
    const popupSwatches = document.querySelectorAll('[data-close-popup]');
    popupSwatches.forEach(function(swatch) {
      swatch.addEventListener('click', function() {
        const popup = this.closest('.color-popup-overlay');
        if (popup) {
          // Small delay to allow the selection to register
          setTimeout(function() {
            closePopup(popup);
          }, 150);
        }
      });
    });

    // Close popup on escape key
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') {
        const activePopup = document.querySelector('.color-popup-overlay.is-active');
        if (activePopup) {
          closePopup(activePopup);
        }
      }
    });
  }

  function openPopup(popup) {
    popup.classList.add('is-active');
    document.body.style.overflow = 'hidden';

    // Force reflow for animation
    popup.offsetHeight;
  }

  function closePopup(popup) {
    popup.classList.remove('is-active');
    document.body.style.overflow = '';
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initColorPopup);
  } else {
    initColorPopup();
  }

  // Re-initialize when Shopify sections are loaded/reloaded
  document.addEventListener('shopify:section:load', initColorPopup);
})();
